import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, Play, ExternalLink } from "lucide-react";

// Configure the PDF.js worker (must match installed pdfjs-dist version)
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const PDF_FILE = "/ferum-pitch-deck.pdf";
const YT_PLAYLIST =
  "https://youtube.com/playlist?list=PLJW3VhK8TedcCX1dpxpdVHLOd0O7Xx_Nt&si=07XcUstGdZKOHLEL";

const documentOptions = {
  cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
  cMapPacked: true,
};

export const Slideshow = () => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageWidth, setPageWidth] = useState(960);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);

  // Responsive page width based on viewport
  useEffect(() => {
    const updateSize = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = window.innerHeight - 200; // leave space for header + controls
      // Pitch deck slides are landscape ~16:9; compute width that fits height too
      const widthByHeight = h * (16 / 9);
      setPageWidth(Math.max(320, Math.min(w, widthByHeight)));
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [loaded]);

  const goPrev = useCallback(() => {
    setPageNumber((p) => Math.max(1, p - 1));
  }, []);

  const goNext = useCallback(() => {
    setPageNumber((p) => Math.min(numPages || p, p + 1));
  }, [numPages]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        goNext();
      } else if (e.key === "Home") {
        setPageNumber(1);
      } else if (e.key === "End" && numPages) {
        setPageNumber(numPages);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, numPages]);

  const onDocumentLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
    setLoaded(true);
  };

  const isLastPage = numPages > 0 && pageNumber === numPages;

  // Pre-compute counter padding length for monospaced look
  const counterDigits = useMemo(
    () => String(numPages || 12).length,
    [numPages]
  );

  return (
    <div className="min-h-screen w-full bg-[#0a0a0b] text-white flex flex-col font-manrope selection:bg-white/20">
      {/* Slide viewport */}
      <main
        ref={containerRef}
        className="flex-1 flex flex-col items-center justify-center px-4 sm:px-10 py-6"
        data-testid="slide-viewport"
      >
        <div
          className="relative rounded-xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/10 bg-black"
          data-testid={`slide-${pageNumber}`}
        >
          <Document
            file={PDF_FILE}
            onLoadSuccess={onDocumentLoadSuccess}
            options={documentOptions}
            loading={
              <div className="flex items-center justify-center w-[80vw] max-w-[960px] aspect-video bg-neutral-900">
                <div className="flex items-center gap-3 text-neutral-400 text-sm">
                  <span className="inline-block h-2 w-2 rounded-full bg-white/60 animate-pulse" />
                  Loading pitch deck…
                </div>
              </div>
            }
            error={
              <div className="flex items-center justify-center w-[80vw] max-w-[960px] aspect-video bg-neutral-900 text-red-400 text-sm">
                Could not load the PDF. Please refresh.
              </div>
            }
          >
            <Page
              key={`page_${pageNumber}`}
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={true}
              loading={
                <div
                  className="bg-neutral-900"
                  style={{
                    width: pageWidth,
                    height: (pageWidth * 9) / 16,
                  }}
                />
              }
            />
          </Document>

          {/* Overlay click target for the LAST slide so the YouTube link is
              guaranteed to be clickable, on top of native PDF annotations */}
          {isLastPage && (
            <a
              href={YT_PLAYLIST}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="last-slide-link-overlay"
              aria-label="Open YouTube playlist"
              className="absolute inset-0 cursor-pointer group"
            >
              <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white text-black text-xs font-semibold px-4 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <Play className="w-3 h-3" fill="currentColor" />
                Watch on YouTube
                <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          )}
        </div>
      </main>

      {/* Bottom controls */}
      <footer className="border-t border-white/5 px-6 sm:px-10 py-5">
        <div className="mx-auto flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={goPrev}
            disabled={pageNumber <= 1}
            data-testid="prev-slide-btn"
            aria-label="Previous slide"
            className="group flex items-center justify-center h-11 w-11 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/10 hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
          </button>

          <div
            className="flex items-baseline gap-2 tabular-nums font-mono"
            data-testid="page-counter"
          >
            <span className="text-white text-lg font-semibold">
              {String(pageNumber).padStart(counterDigits, "0")}
            </span>
            <span className="text-neutral-600 text-sm">/</span>
            <span className="text-neutral-500 text-sm">
              {numPages ? String(numPages).padStart(counterDigits, "0") : "—"}
            </span>
          </div>

          <button
            type="button"
            onClick={goNext}
            disabled={!numPages || pageNumber >= numPages}
            data-testid="next-slide-btn"
            aria-label="Next slide"
            className="group flex items-center justify-center h-11 w-11 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/10 hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </footer>
      {/* Floating Watch-the-MVP CTA (bottom-right) */}
      <a
        href={YT_PLAYLIST}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="watch-mvp-cta"
        className="fixed bottom-5 right-5 z-[9999] inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2.5 text-[11px] font-bold tracking-[0.14em] uppercase shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] hover:-translate-y-0.5 hover:shadow-[0_14px_34px_-10px_rgba(0,0,0,0.75)] transition-all"
      >
        <Play className="w-3 h-3" fill="currentColor" />
        Watch the MVP
      </a>
    </div>
  );
};

export default Slideshow;
